import { useState, useEffect } from 'react';
import { ordersAPI } from '../api';
import { RefreshCw } from 'react-feather';
import './AdminOrders.css';

const STATUS_COLORS = {
  pending: 'badge-pending',
  in_progress: 'badge-in_progress',
  completed: 'badge-completed',
  partial: 'badge-partial',
  canceled: 'badge-canceled',
  refunded: 'badge-canceled',
};

function getDisplayStatus(order) {
  return order?.refund_status === 'refunded' || order?.refund_status === 'manual_refunded'
    ? 'refunded'
    : order?.status;
}

function getActionLabel(order) {
  const displayStatus = getDisplayStatus(order);

  switch (displayStatus) {
    case 'completed':
      return 'completed';
    case 'refunded':
      return 'refunded';
    case 'in_progress':
      return 'in progress';
    case 'pending':
      return 'pending';
    case 'partial':
      return 'partial';
    case 'canceled':
      return 'canceled';
    default:
      return String(displayStatus || 'pending').replace('_', ' ');
  }
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [refundModal, setRefundModal] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundMsg, setRefundMsg] = useState('');
  const [refunding, setRefunding] = useState(false);

  const fetchOrders = async (p = 1) => {
    setLoading(true);
    try {
      const res = await ordersAPI.adminRecent();
      setOrders(res.data.orders || []);
      setTotal(res.data.total);
      setPage(1);
      setPages(1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(1);
  }, []);

  const openRefund = (order) => {
    setRefundModal(order);
    setRefundAmount(parseFloat(order.cost).toFixed(4));
    setRefundMsg('');
  };

  const handleRefund = async () => {
    if (!refundAmount || parseFloat(refundAmount) <= 0) return;
    setRefunding(true);
    setRefundMsg('');

    try {
      await ordersAPI.adminRefund(refundModal.id, parseFloat(refundAmount));
      setRefundMsg('Refund applied successfully');
      setOrders((prev) => prev.map((o) => (
        o.id === refundModal.id
          ? { ...o, refund_status: 'manual_refunded', refund_amount: refundAmount }
          : o
      )));
      setTimeout(() => setRefundModal(null), 1400);
    } catch (err) {
      setRefundMsg('Refund failed: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setRefunding(false);
    }
  };

  const filtered = orders.filter((o) => {
    const displayStatus = getDisplayStatus(o);
    const matchSearch = !search
      || o.username?.toLowerCase().includes(search.toLowerCase())
      || String(o.id).includes(search)
      || o.service_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || displayStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title">All Orders</h1>
          <p className="page-subtitle" style={{ marginBottom: 0 }}>{total} total orders</p>
        </div>
        <button
          className="btn btn-secondary btn-sm admin-orders-refresh-btn"
          onClick={() => fetchOrders(page)}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <input
            className="form-input"
            style={{ maxWidth: 280 }}
            placeholder="Search by user, order ID, service..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="form-select"
            style={{ maxWidth: 180 }}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            {['pending', 'in_progress', 'completed', 'partial', 'canceled', 'refunded'].map((status) => (
              <option key={status} value={status}>{status.replace('_', ' ')}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner" style={{ margin: '0 auto' }} />
          </div>
        ) : (
          <>
            <div className="table-wrap admin-orders-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Service</th>
                    <th>Link</th>
                    <th>Qty</th>
                    <th>Cost</th>
                    <th>Remains</th>
                    <th>Status</th>
                    <th>Refund</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((order) => {
                    const displayStatus = getDisplayStatus(order);

                    return (
                      <tr key={order.id}>
                        <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 12 }}>{order.id}</td>
                        <td>
                          <div className="admin-orders-username">{order.username}</div>
                          <div className="admin-orders-email">{order.email}</div>
                        </td>
                        <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                          {order.service_name}
                        </td>
                        <td>
                          <a
                            href={order.link}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: 'var(--accent)', fontSize: 12, maxWidth: 120, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {order.link}
                          </a>
                        </td>
                        <td style={{ fontSize: 13 }}>{parseInt(order.quantity).toLocaleString()}</td>
                        <td style={{ color: 'var(--orange)', fontWeight: 600, fontSize: 13 }}>${parseFloat(order.cost).toFixed(4)}</td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{order.remains || '-'}</td>
                        <td>
                          <span className={`badge ${STATUS_COLORS[displayStatus] || 'badge-pending'}`} style={{ fontSize: 11 }}>
                            {displayStatus.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          {order.refund_status === 'refunded' && (
                            <span style={{ color: 'var(--green-500)', fontSize: 12, fontWeight: 600 }}>
                              Auto ${parseFloat(order.refund_amount).toFixed(4)}
                            </span>
                          )}
                          {order.refund_status === 'manual_refunded' && (
                            <span style={{ color: 'var(--orange)', fontSize: 12, fontWeight: 600 }}>
                              Manual ${parseFloat(order.refund_amount).toFixed(4)}
                            </span>
                          )}
                          {(order.refund_status === 'pending' || !order.refund_status) && (
                            <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>-</span>
                          )}
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <span className={`badge ${STATUS_COLORS[displayStatus] || 'badge-pending'}`} style={{ fontSize: 11 }}>
                            {getActionLabel(order)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={11} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                        No orders found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pages > 1 && (
              <div className="pagination">
                {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
                  <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => fetchOrders(p)}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {refundModal && (
        <div className="modal-overlay" onClick={() => setRefundModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Manual Refund</h3>
            {refundMsg && (
              <div className={`alert ${refundMsg.startsWith('Refund applied') ? 'alert-success' : 'alert-error'}`}>
                {refundMsg}
              </div>
            )}
            <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 20 }}>
              {[
                ['Order #', refundModal.id],
                ['User', refundModal.username],
                ['Service', refundModal.service_name],
                ['Order Cost', `$${parseFloat(refundModal.cost).toFixed(4)}`],
                ['Quantity', parseInt(refundModal.quantity).toLocaleString()],
                ['Status', getDisplayStatus(refundModal).replace('_', ' ')],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                  <span style={{ fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
            <div className="form-group">
              <label className="form-label">Refund Amount (USD)</label>
              <input
                className="form-input"
                type="number"
                step="0.0001"
                min="0.0001"
                max={parseFloat(refundModal.cost)}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
              />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Max: ${parseFloat(refundModal.cost).toFixed(4)} (full order cost)
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setRefundModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={handleRefund} disabled={refunding}>
                {refunding ? 'Processing...' : 'Apply Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
